import type { AxiosInstance } from 'axios';
import type { TranslateFn, U2000ApiErrorResult } from '../u2000Core/axios/U2000HandleApiError';
import type { U2000SearchPayload } from '../u2000Core/components/tables/basic/U2000BasicTable';
import { U2000GenerateUrlUUID } from '../u2000Core/tools/U2000UrlHelpers';
import {
  u2000GetActiveDisplayTimezone,
  u2000ReadWallClock,
} from '../u2000Core/tools/U2000DateTimeHelpers';

const LOGICALUNIT_URL = 'U3208';
const U2200_JOB_GET = (jobId: string) => `U2200/jobs/${jobId}`;

type LogicalUnitEndpointsType = {
  INITLU: string;
  SEARCH: string;
  GET: (id: string | number) => string;
  CREATE: string;
  UPDATE: (id: string | number) => string;
  DELETE: (id: string | number) => string;
  SOURCES: (corpusId: string | number) => string;
  UPLOAD: (corpusId: string | number) => string;
  SOURCE_GET: (sourceId: string | number) => string;
  SOURCE_DELETE: (sourceId: string | number) => string;
  SOURCE_REINDEX: (sourceId: string | number) => string;
  CORPUS_REINDEX: (corpusId: string | number) => string;
};

const LogicalUnitEndpoints: LogicalUnitEndpointsType = {
  INITLU: `${LOGICALUNIT_URL}/initLU`,
  SEARCH: `${LOGICALUNIT_URL}/search`,
  GET: (id) => `${LOGICALUNIT_URL}/${id}`,
  CREATE: `${LOGICALUNIT_URL}/create`,
  UPDATE: (id) => `${LOGICALUNIT_URL}/update/${id}`,
  DELETE: (id) => `${LOGICALUNIT_URL}/delete/${id}`,
  SOURCES: (corpusId) => `${LOGICALUNIT_URL}/${corpusId}/sources`,
  UPLOAD: (corpusId) => `${LOGICALUNIT_URL}/${corpusId}/sources/upload`,
  SOURCE_GET: (sourceId) => `${LOGICALUNIT_URL}/sources/${sourceId}`,
  SOURCE_DELETE: (sourceId) => `${LOGICALUNIT_URL}/sources/${sourceId}/delete`,
  SOURCE_REINDEX: (sourceId) => `${LOGICALUNIT_URL}/sources/${sourceId}/reindex`,
  CORPUS_REINDEX: (corpusId) => `${LOGICALUNIT_URL}/${corpusId}/reindex`,
};

export type U3208AllowedMimeType = 'text/plain' | 'text/markdown' | 'application/pdf';

export interface U3208InitLU {
  launchSearch: boolean;
  hideSearch: boolean;
  nbRecordPerPage: number;
  logicalUnitCode: string;
  allowedMimeTypes: U3208AllowedMimeType[];
  maxFileSizeBytes: number;
}

export interface U3208AiRagCorpusSearchRow {
  id: string;
  code: string;
  title: string;
  active: boolean;
  systemScope: boolean;
  organizationId: string | null;
  version: number;
}

export interface U3208AiRagCorpusFormRow {
  id: string;
  code: string;
  title: string;
  active: boolean;
  systemScope: boolean;
  organizationId: string | null;
  version: number;
}

export interface U3208AiRagCorpusCreateBody {
  code: string;
  title: string;
  active: boolean;
}

export interface U3208AiRagCorpusUpdateBody {
  title: string;
  active: boolean;
  version: number;
}

export interface U3208AiRagCorpusDeleteBody {
  version: number;
}

export interface U3208DocSource {
  id: string;
  corpusId: string;
  logicalUnitCode: string;
  sourcePath: string;
  contentHash: string;
  revision: number;
  indexedAt: string | null;
  /** Null pour sources système Product Help (affichage via sourcePath). */
  displayName: string | null;
  mimeType: string | null;
  fileSize: number | null;
  originalFilename: string | null;
  chunkCount: number;
  version?: number;
}

export type U3208U2200JobStatus = 'pending' | 'running' | 'completed' | 'failed';

export interface U3208U2200Job {
  id: string;
  jobType: string;
  status: U3208U2200JobStatus;
  attempts?: number;
  maxAttempts?: number;
  version?: number;
  result?: {
    success?: boolean;
    message?: string;
    data?: unknown;
  } | null;
  errorMessage?: string | null;
}

export type U3208SourceStatus = 'pending' | 'indexed' | 'failed';

export const U3208_ERROR_ORG_MISMATCH = 'BU320801';
export const U3208_ERROR_SYSTEM_IMMUTABLE = 'BU320802';
export const U3208_ERROR_FILE_TOO_LARGE = 'BU320803';
export const U3208_ERROR_UNEXPECTED_FIELD = 'BU320804';
export const U3208_ERROR_MISSING_FILE = 'BU320805';
export const U3208_ERROR_UNSUPPORTED_TYPE = 'BU320806';
export const U3208_ERROR_CODE_DUPLICATE = 'BU320807';
export const U3208_ERROR_OPTIMISTIC_LOCK = 'BU320808';
export const U3208_ERROR_LINKED_MISSION = 'BU320809';
export const U3208_ERROR_PATH_DUPLICATE = 'BU320810';

export const U3208_DEFAULT_MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
export const U3208_DEFAULT_ALLOWED_MIME: U3208AllowedMimeType[] = [
  'text/plain',
  'text/markdown',
  'application/pdf',
];

/** Corpus système : détection de portée, indépendamment des droits UI du profil courant. */
export function u3208IsSystemCorpus(row: {
  id?: string;
  systemScope?: boolean;
  organizationId?: string | null;
}): boolean {
  if (row.systemScope === true) return true;
  if (row.id && row.organizationId == null) return true;
  return false;
}

export function u3208DeriveSourceStatus(
  source: Pick<U3208DocSource, 'indexedAt' | 'chunkCount'>,
  jobFailed = false,
): U3208SourceStatus {
  if (jobFailed) return 'failed';
  if (source.indexedAt) return 'indexed';
  return 'pending';
}

/** Libellé document : displayName → originalFilename → sourcePath. */
export function u3208SourceDisplayName(source: Pick<U3208DocSource, 'displayName' | 'originalFilename' | 'sourcePath'>): string {
  const name = (source.displayName ?? '').trim() || (source.originalFilename ?? '').trim();
  if (name) return name;
  return (source.sourcePath ?? '').trim() || '—';
}

/**
 * Parse la liste sources (`U2800ResultsResponseDto.results`, comme U3204 Product Help).
 * Accepte aussi `result` / tableau plat pour robustesse.
 */
export function u3208ParseSourcesList(data: unknown): U3208DocSource[] {
  if (!data || typeof data !== 'object') return [];
  const envelope = data as { results?: unknown; result?: unknown };
  if (Array.isArray(envelope.results)) return envelope.results as U3208DocSource[];
  if (Array.isArray(envelope.result)) return envelope.result as U3208DocSource[];
  if (Array.isArray(data)) return data as U3208DocSource[];
  const nested = envelope.result;
  if (nested && typeof nested === 'object' && Array.isArray((nested as { data?: unknown }).data)) {
    return (nested as { data: U3208DocSource[] }).data;
  }
  return [];
}

export function u3208FormatIndexedAtOrg(iso: string | null | undefined): string {
  if (iso == null || String(iso).trim() === '') return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const p = u2000ReadWallClock(d.getTime(), u2000GetActiveDisplayTimezone());
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${p.y}-${pad(p.mo)}-${pad(p.d)} ${pad(p.h)}:${pad(p.mi)}:${pad(p.s)}`;
}

export function u3208MimeTypesToAccept(mimeTypes: U3208AllowedMimeType[]): string {
  const ext: string[] = [];
  for (const mime of mimeTypes) {
    if (mime === 'text/plain') ext.push('.txt');
    else if (mime === 'text/markdown') ext.push('.md');
    else if (mime === 'application/pdf') ext.push('.pdf');
  }
  return ext.join(',');
}

export function u3208IsAllowedUploadFile(
  file: File,
  allowedMimeTypes: U3208AllowedMimeType[],
  maxFileSizeBytes: number,
): 'ok' | 'tooLarge' | 'unsupported' {
  if (file.size > maxFileSizeBytes) return 'tooLarge';
  const name = file.name.toLowerCase();
  const mime = file.type as U3208AllowedMimeType | '';
  const allowedByMime = mime !== '' && allowedMimeTypes.includes(mime as U3208AllowedMimeType);
  const allowedByExt =
    (name.endsWith('.txt') && allowedMimeTypes.includes('text/plain')) ||
    (name.endsWith('.md') && allowedMimeTypes.includes('text/markdown')) ||
    (name.endsWith('.pdf') && allowedMimeTypes.includes('application/pdf'));
  if (!allowedByMime && !allowedByExt) return 'unsupported';
  return 'ok';
}

export function createEmptyU3208FormRow(): U3208AiRagCorpusFormRow {
  return {
    id: '',
    code: '',
    title: '',
    active: true,
    systemScope: false,
    organizationId: '',
    version: 0,
  };
}

export function normalizeU3208CorpusFromApi(row: U3208AiRagCorpusFormRow): U3208AiRagCorpusFormRow {
  return {
    id: row.id,
    code: row.code,
    title: row.title,
    active: row.active,
    systemScope: Boolean(row.systemScope),
    organizationId: row.organizationId ?? null,
    version: row.version,
  };
}

export function buildCreateBody(data: U3208AiRagCorpusFormRow): U3208AiRagCorpusCreateBody {
  return {
    code: data.code.trim().toUpperCase(),
    title: data.title.trim(),
    active: data.active,
  };
}

export function buildUpdateBody(data: U3208AiRagCorpusFormRow): U3208AiRagCorpusUpdateBody {
  return {
    title: data.title.trim(),
    active: data.active,
    version: data.version,
  };
}

export function buildDeleteBody(data: U3208AiRagCorpusFormRow): U3208AiRagCorpusDeleteBody {
  return { version: data.version };
}

/** Extrait le job depuis une réponse U2200 (enveloppe `{ result }` ou job plat). */
export function u3208ParseU2200JobResponse(data: unknown): U3208U2200Job | null {
  if (!data || typeof data !== 'object') return null;
  const envelope = data as { result?: unknown; id?: unknown; status?: unknown };
  const raw = envelope.result && typeof envelope.result === 'object' ? envelope.result : data;
  if (!raw || typeof raw !== 'object') return null;
  const job = raw as U3208U2200Job;
  if (typeof job.id !== 'string' || typeof job.status !== 'string') return null;
  return job;
}

export function u3208ParseUploadResponse(data: unknown): { source: U3208DocSource; job: U3208U2200Job } | null {
  if (!data || typeof data !== 'object') return null;
  const envelope = data as { result?: unknown; job?: unknown };
  const sourceRaw = envelope.result;
  if (!sourceRaw || typeof sourceRaw !== 'object') return null;
  const source = sourceRaw as U3208DocSource;
  if (typeof source.id !== 'string') return null;
  const job = u3208ParseU2200JobResponse(envelope.job);
  if (!job) return null;
  return { source, job };
}

export const doInitLU = (axios: AxiosInstance) => {
  const params = { b: U2000GenerateUrlUUID() };
  return axios.get(LogicalUnitEndpoints.INITLU, { params });
};

export const doSearch = (axios: AxiosInstance, searchPayload: U2000SearchPayload) =>
  axios.post(LogicalUnitEndpoints.SEARCH, searchPayload);

export const doGetData = (axios: AxiosInstance, id: string | number, forceRefresh?: boolean) => {
  const params = { ...(forceRefresh && { b: U2000GenerateUrlUUID() }) };
  return axios.get(LogicalUnitEndpoints.GET(id), { params });
};

export const doCreateData = (axios: AxiosInstance, body: U3208AiRagCorpusCreateBody) =>
  axios.post(LogicalUnitEndpoints.CREATE, body);

export const doUpdateData = (axios: AxiosInstance, id: string | number, body: U3208AiRagCorpusUpdateBody) =>
  axios.post(LogicalUnitEndpoints.UPDATE(id), body);

export const doDeleteData = (axios: AxiosInstance, id: string | number, body: U3208AiRagCorpusDeleteBody) =>
  axios.post(LogicalUnitEndpoints.DELETE(id), body);

export const doListSources = (axios: AxiosInstance, corpusId: string | number) => {
  const params = { b: U2000GenerateUrlUUID() };
  return axios.get(LogicalUnitEndpoints.SOURCES(corpusId), { params });
};

export const doUploadSource = (axios: AxiosInstance, corpusId: string | number, formData: FormData) =>
  axios.post(LogicalUnitEndpoints.UPLOAD(corpusId), formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

export const doGetSource = (axios: AxiosInstance, sourceId: string | number) => {
  const params = { b: U2000GenerateUrlUUID() };
  return axios.get(LogicalUnitEndpoints.SOURCE_GET(sourceId), { params });
};

export const doDeleteSource = (axios: AxiosInstance, sourceId: string | number) =>
  axios.post(LogicalUnitEndpoints.SOURCE_DELETE(sourceId), {});

export const doReindexSource = (axios: AxiosInstance, sourceId: string | number) =>
  axios.post(LogicalUnitEndpoints.SOURCE_REINDEX(sourceId), {});

export const doReindexCorpus = (axios: AxiosInstance, corpusId: string | number) =>
  axios.post(LogicalUnitEndpoints.CORPUS_REINDEX(corpusId), {});

export const doGetU2200Job = (axios: AxiosInstance, jobId: string) =>
  axios.get<{ result?: U3208U2200Job } & U3208U2200Job>(U2200_JOB_GET(jobId));

export function luHandleAppiError(
  t: TranslateFn,
  nameSpace: string,
  err: unknown,
  errorResult: U2000ApiErrorResult,
): U2000ApiErrorResult {
  const apiErr = err as { errorCode?: string };
  const code = apiErr?.errorCode ?? '';
  const title = t('errorTitle', { ns: nameSpace });

  const keyByCode: Record<string, string> = {
    [U3208_ERROR_ORG_MISMATCH]: 'error.orgMismatch',
    [U3208_ERROR_SYSTEM_IMMUTABLE]: 'error.systemImmutable',
    [U3208_ERROR_FILE_TOO_LARGE]: 'error.fileTooLarge',
    [U3208_ERROR_UNEXPECTED_FIELD]: 'error.unexpectedField',
    [U3208_ERROR_MISSING_FILE]: 'error.missingFile',
    [U3208_ERROR_UNSUPPORTED_TYPE]: 'error.unsupportedType',
    [U3208_ERROR_CODE_DUPLICATE]: 'error.codeDuplicate',
    [U3208_ERROR_OPTIMISTIC_LOCK]: 'error.optimisticLock',
    [U3208_ERROR_LINKED_MISSION]: 'error.linkedMission',
    [U3208_ERROR_PATH_DUPLICATE]: 'error.pathDuplicate',
  };

  const messageKey = keyByCode[code];
  if (messageKey) {
    errorResult.genericErrors = [
      ...(errorResult.genericErrors ?? []),
      { title, text: t(messageKey, { ns: nameSpace }) },
    ];
  }
  return errorResult;
}
